import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { FlexContainer, Button } from "../../styles/sharedStyles";
import { formatTime } from "../../utils/formatTime";
import { ReactComponent as AllIcon } from "@/assets/result/result_ss/ss_all.svg?react";
import { ReactComponent as VocalsIcon } from "@/assets/result/result_ss/ss_vocal.svg?react";
import { ReactComponent as BassIcon } from "@/assets/result/result_ss/ss_bass.svg?react";
import { ReactComponent as DrumsIcon } from "@/assets/result/result_ss/ss_drum.svg?react";
import { ReactComponent as OtherIcon } from "@/assets/result/result_ss/ss_other.svg?react";
import { ReactComponent as PianoIcon } from "@/assets/result/result_ss/ss_piano.svg?react";
import { ReactComponent as PlayIcon } from "@/assets/result/result_ss/ss_play.svg?react";
import { ReactComponent as PauseIcon } from "@/assets/result/result_ss/ss_pause.svg?react";
import { ReactComponent as VolumeIcon } from "@/assets/result/result_ss/ss_volume.svg?react";
import { ReactComponent as MuteIcon } from "@/assets/result/result_ss/ss_mute.svg?react";
import { ReactComponent as DownloadIcon } from "@/assets/result/download.svg?react";
import { ReactComponent as SettingsIcon } from "@/assets/result/settings.svg?react";
import { formatSpeakerName } from "utils/formatSpeaker";

interface ContainerProps {
  isSelected: boolean;
  disabled?: boolean;
}

interface TitleProps {
  isSelected: boolean;
  trackId: TrackId;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ isSelected }) => (isSelected ? "#30B0C7" : "#D9D9D9")};
  border-radius: 10px;
  padding: 12px 12px 4px 12px;
  width: 100%;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  background-color: ${({ isSelected }) =>
    isSelected ? "#F0FAFC" : "transparent"};
  transition: background-color 0.2s, border-color 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const TopRow = styled(FlexContainer)`
  justify-content: space-between;
  padding: 4px 8px;

  .icon {
    width: 18px;
    height: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const Title = styled(FlexContainer)<TitleProps>`
  gap: 4px;

  span {
    color: #5a5a5a;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  svg {
    color: ${({ isSelected }) => (isSelected ? "#30B0C7" : "#D9D9D9")};
    width: ${({ trackId }) => (trackId === "vocals" ? "10px" : "12px")};
  }
`;

const DownloadButton = styled(Button)<{ disabled?: boolean }>`
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  padding: 0;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  svg {
    height: 15px;
  }
`;

const SettingButton = styled(Button)`
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  padding: 0;

  svg {
    height: 13px;
  }
`;

const PlayerControls = styled(FlexContainer)`
  padding: 8px;
`;

const PlayPauseButton = styled(Button)<{ disabled?: boolean }>`
  border: none;
  background: none;
  padding: 0;
  margin-right: 8px;
  height: 16px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  .icon {
    width: 16px;
    height: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  svg {
    color: #30b0c7;
    height: 10px;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  margin-right: 12px;
  height: 2px;
  background-color: rgba(222, 226, 230, 0.83);
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
`;

const ProgressFilled = styled.div`
  height: 100%;
  background-color: #30b0c7;
  border-radius: 4px;
`;

const Time = styled.span`
  color: #5a5a5a;
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
  margin-right: 12px;
`;

const VolumeButton = styled(Button)`
  border: none;
  background: none;
  height: 24px;

  svg {
    height: 16px;
  }
`;

export type InstrumentType =
  | "all"
  | "vocals"
  | "bass"
  | "drums"
  | "other"
  | "piano";

export type TrackId = InstrumentType | string; // Allow speaker IDs

interface CustomAudioPlayerProps {
  trackId: TrackId;
  audioUrl: string;
  isSelected?: boolean;
  isPlaying?: boolean;
  isAnyInstrumentSelected?: boolean;
  syncedTime?: number;
  disabled?: boolean;
  onSelect?: (trackId: TrackId) => void;
  onPlayPause?: () => void;
  onTimeUpdate?: (trackId: TrackId, time: number) => void;
  onEnded?: (trackId: TrackId) => void;
}

const getIcon = (trackId: TrackId) => {
  if (typeof trackId === "string" && trackId.startsWith("SPEAKER")) {
    return <VocalsIcon />;
  }

  switch (trackId) {
    case "all":
      return <AllIcon />;
    case "vocals":
      return <VocalsIcon />;
    case "bass":
      return <BassIcon />;
    case "drums":
      return <DrumsIcon />;
    case "other":
      return <OtherIcon />;
    case "piano":
      return <PianoIcon />;
    default:
      return <AllIcon />;
  }
};

const CustomAudioPlayer: React.FC<CustomAudioPlayerProps> = ({
  trackId,
  audioUrl,
  isSelected: isSelectedProp,
  isPlaying: isGroupPlaying,
  isAnyInstrumentSelected,
  syncedTime,
  disabled = false,
  onSelect,
  onPlayPause,
  onTimeUpdate,
  onEnded,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isSoloPlaying, setIsSoloPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);

  const instrumentMap: Record<InstrumentType, string> = {
    all: "전체",
    vocals: "보컬",
    bass: "베이스",
    drums: "드럼",
    piano: "피아노",
    other: "그 외",
  };

  const getTrackName = (id: TrackId) => {
    if (typeof id === "string" && id.startsWith("SPEAKER")) {
      return formatSpeakerName(id);
    }
    return instrumentMap[id as InstrumentType] || "알 수 없음";
  };

  const isGroupMode = typeof isSelectedProp !== "undefined";

  const isPlaying = isGroupMode
    ? (isSelectedProp && isGroupPlaying) ||
      (!isAnyInstrumentSelected && isSoloPlaying)
    : isSoloPlaying;
  const isSelected = isGroupMode ? isSelectedProp : false;

  useEffect(() => {
    if (
      isGroupMode &&
      isSelected &&
      typeof syncedTime === "number" &&
      audioRef.current
    ) {
      if (Math.abs(audioRef.current.currentTime - syncedTime) > 1) {
        audioRef.current.currentTime = syncedTime;
      }
    }
  }, [syncedTime, isGroupMode, isSelected]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current
        .play()
        .catch((e) => console.error("Audio play failed:", e));
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const togglePlayPause = () => {
    if (disabled) return;
    if (isAnyInstrumentSelected) {
      onPlayPause?.();
    } else {
      setIsSoloPlaying((prev) => !prev);
    }
  };

  const handleContainerClick = () => {
    if (disabled) return;
    if (onSelect) {
      onSelect(trackId);
    } else {
      // 그룹 모드가 아닐 때의 독립적인 재생/일시정지 로직
      setIsSoloPlaying((prev) => !prev);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && !isDragging) {
      setCurrentTime(audioRef.current.currentTime);
      if (onTimeUpdate) {
        onTimeUpdate(trackId, audioRef.current.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !audioRef.current) return;

    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const newTime = (clickPosition / rect.width) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    if (onTimeUpdate) {
      onTimeUpdate(trackId, newTime);
    }
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging && progressBarRef.current && audioRef.current) {
      const progressBar = progressBarRef.current;
      const rect = progressBar.getBoundingClientRect();
      const movePosition = e.clientX - rect.left;
      const newTime = Math.max(
        0,
        Math.min((movePosition / rect.width) * duration, duration)
      );
      setCurrentTime(newTime);
      audioRef.current.currentTime = newTime;
      if (onTimeUpdate) {
        onTimeUpdate(trackId, newTime);
      }
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleAudioEnded = () => {
    setIsSoloPlaying(false);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    if (onEnded) {
      onEnded(trackId);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;

    const isNowMuted = !isMuted;
    setIsMuted(isNowMuted);
    audioRef.current.muted = isNowMuted;
  };

  const downloadAudio = async () => {
    if (disabled) return;
    try {
      const response = await fetch(audioUrl);
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${trackId}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(
        "There has been a problem with your fetch operation:",
        error
      );
    }
  };

  const calculateProgress = (): number => {
    return (currentTime / duration) * 100 || 0;
  };

  return (
    <Container
      isSelected={isSelected}
      onClick={handleContainerClick}
      disabled={disabled}
    >
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleAudioEnded}
        muted={isMuted}
      />
      <TopRow>
        <Title isSelected={isSelected} trackId={trackId}>
          <div className="icon">{getIcon(trackId)}</div>
          <span>{getTrackName(trackId)}</span>
        </Title>
        <FlexContainer>
          <DownloadButton onClick={downloadAudio} disabled={disabled}>
            <DownloadIcon />
          </DownloadButton>
          <SettingButton>
            <SettingsIcon />
          </SettingButton>
        </FlexContainer>
      </TopRow>
      <PlayerControls>
        <PlayPauseButton
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            togglePlayPause();
          }}
        >
          <div className="icon">{isPlaying ? <PauseIcon /> : <PlayIcon />}</div>
        </PlayPauseButton>
        <ProgressBar
          ref={progressBarRef}
          onClick={(e) => {
            e.stopPropagation();
            handleProgressClick(e);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleMouseDown();
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <ProgressFilled style={{ width: `${calculateProgress()}%` }} />
        </ProgressBar>
        <Time>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Time>
        <VolumeButton
          onClick={(e) => {
            e.stopPropagation();
            toggleMute();
          }}
        >
          {isMuted ? <MuteIcon /> : <VolumeIcon />}
        </VolumeButton>
      </PlayerControls>
    </Container>
  );
};

export default CustomAudioPlayer;
